import React from "react";
import { useSelector,useDispatch } from "react-redux";

// mui components
import { Box, InputLabel, Stack } from "@mui/material";

// custom components
import OutlinedTextInput from "../../custom-input/outlined-text-input";
import SecondarySelect from "../../custom-select/secondary-select";
import SecondarySelectLOLP from "../../custom-select/secondary-select-lo-lp";
import CustomDivider from "../../custom-divider";
import Heading5 from "../../typography/heading-05";
import Heading6 from "../../typography/heading-06";
import Body3 from "../../typography/body-03";

// redux slice
import { selectUserDetails, selectSelectedOrganization } from "../../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";

// helper utils
import { stateOptions } from "../../../utils/select-options";
import { selectedUserWithLoanChannel } from "../../../utils/common-helpers";
import { LOAN_CHANNEL_CONSTANTS } from "../../../utils/common-constants";

// custom styles
import styles from "./index.module.scss"; 

const LoanProcessor = ({
	organizationName,
	organizationDetails,
	loanProcessorEmail,
	setLoanProcessorEmail,
	loanProcessorId,
	setLoanProcessorId,
	organizationProcessor,
	loanProcessor,
	viewLoan,
	disabled,
	error,
}) => {
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);

	return (
		<Box className={styles.cardContainer}>
			<Stack direction="column" className={styles.cardHeaderContainer}>
				<Heading5 text="Loan Processor Information" fontType="semibold" />
				<Body3 text="Provide details about the loan processor" />
			</Stack>
			<Stack direction="column" className={styles.formSectionContainer}>
				{/* organization information */}
				<Stack direction="row" className={styles.inputSectionContainer}>
					{/* organization name */}
					<Stack
						direction="column"
						className={styles.inputContainer + " " + styles.fullWidthContainer}
					>
						<InputLabel htmlFor="outlined-input-processor-organization-name">
							<Heading6
								text="Organization Name"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-processor-organization-name"
							type="text"
							placeholder="Organization Name"
							fullWidth={true}
							value={organizationName}
							disabled={true}
						/>
					</Stack>

					{/* address line */}
					<Stack
						direction="column"
						className={styles.inputContainer + " " + styles.fullWidthContainer}
					>
						<InputLabel htmlFor="outlined-input-processor-address-line">
							<Heading6
								text="Address Line 1"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-processor-address-line"
							type="text"
							placeholder="Address Line"
							fullWidth={true}
							value={organizationDetails?.address}
							disabled={true}
						/>
					</Stack>

					{/* unit number */}
					<Stack
						direction="column"
						className={styles.inputContainer + " " + styles.fullWidthContainer}
					>
						<InputLabel htmlFor="outlined-input-processor-unit-number">
							<Heading6
								text="Address Line 2 / Unit Number"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-processor-unit-number"
							type="text"
							placeholder="Unit Number"
							fullWidth={true}
							// value={data?.addressUnitIdentifier}
							disabled={true}
						/>
					</Stack>

					{/* city */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="outlined-input-processor-city">
							<Heading6
								text="City"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-processor-city"
							type="text"
							placeholder="City"
							value={organizationDetails?.city}
							disabled={true}
						/>
					</Stack>

					{/* state */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="select-processor-state">
							<Heading6
								text="State"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<SecondarySelect
							id="select-processor-state"
							displayEmpty={true}
							value={organizationDetails?.state || ""}
							options={stateOptions}
							disabled={true}
						/>
					</Stack>

					{/* postal code */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="outlined-input-processor-postal-code">
							<Heading6
								text="Postal Code"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-processor-postal-code"
							type="text"
							placeholder="Postal Code"
							value={organizationDetails?.zip}
							disabled={true}
						/>
					</Stack>
				</Stack>

				{/* loan processor information */}
				<Stack direction="row" className={styles.inputSectionContainer}>
					{/* LP email */}
					<Stack
						direction="column"
						className={styles.inputContainer + " " + styles.fullWidthContainer}
					>
						<InputLabel htmlFor="select-lp-email">
							<Heading6
								text="Loan Processor"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<SecondarySelectLOLP
							id="select-lp-email"
							displayEmpty={true}
							fullWidth={true}
							value={loanProcessorId || ""}
							handleChange={(event) => {
								const selectedId = event.target.value;
								setLoanProcessorId(selectedId);
								const matchedUser = organizationProcessor.find(
									(item) => String(item.broker_id) === String(selectedId)
								);
								setLoanProcessorEmail(matchedUser?.value || "");
							}}
							options={organizationProcessor.sort((a, b) =>
								a.value.localeCompare(b.value)
							  )}
							disabled={disabled}
							error={
								error &&
								(loanProcessorEmail === undefined || loanProcessorEmail === "")
							}
							showOrgName={true}
						/>
					</Stack>
				</Stack>

				<Stack direction="row" className={styles.inputSectionContainer}>
					{/* LP Name */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="outlined-input-lp-name">
							<Heading6
								text="Loan Processor Name"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-lp-name"
							type="text"
							placeholder="LP Name"
							value={
								viewLoan
									? loanProcessor?.user?.name
									: (loanProcessor?.user?.first_name || "") +
									  " " +
									  (loanProcessor?.user?.last_name || "")
							}
							disabled={true}
						/>
					</Stack>

					{/* LO NMLS ID */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="outlined-input-lp-nmls">
							<Heading6
								text="NMLS ID"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-lp-nmls"
							type="text"
							placeholder="NMLS ID"
							value={loanProcessor?.nmls_id}
							disabled={true}
						/>
					</Stack>

					{/* LO phone */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="outlined-input-processor-phone">
							<Heading6
								text="Phone Number"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-processor-phone"
							type="text"
							placeholder="[XXX] XXX-XXXX"
							value={loanProcessor?.phone}
							disabled={true}
						/>
					</Stack>

					{/* LO cell phone */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="outlined-input-processor-cell-phone">
							<Heading6
								text="Cell Phone Number"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-processor-cell-phone"
							type="text"
							placeholder="[XXX] XXX-XXXX"
							value={
								loanProcessor?.cell_phone === null ||
								loanProcessor?.cell_phone === undefined
									? ""
									: loanProcessor?.cell_phone
							}
							disabled={true}
						/>
					</Stack>

					{/* LO email */}
					<Stack direction="column" className={styles.inputContainer}>
						<InputLabel htmlFor="outlined-input-processor-email">
							<Heading6
								text="Email ID"
								fontType="semibold"
								extraClass={styles.inputHeader}
							/>
						</InputLabel>

						<OutlinedTextInput
							id="outlined-input-processor-email"
							type="email"
							placeholder="email@mail.com"
							value={loanProcessor?.user?.email}
							disabled={true}
						/>
					</Stack>
				</Stack>
			</Stack>

			{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && <CustomDivider />}
		</Box>
	);
};

export default LoanProcessor;
